import { IMAGES } from '../constants';

export interface GalleryProjectShowcase {
  id: string;
  title: string;
  subtitle: string;
  cover: string;
  gallery: string[];
  technologies: string[];
  concept: string;
  clientNeed: string;
  artisticNote: string;
}

export interface GalleryScrollItem {
  id: string;
  projectId: string;
}

const projectImage = (projectId: string, file: string) =>
  `/projects/${projectId}/${file.replace(/\.(png|jpe?g)$/i, '.webp')}`;

const projectGallery = (
  projectId: string,
  extraFiles: string[] = []
) => [projectImage(projectId, 'portada.png'), ...extraFiles.map((file) => projectImage(projectId, file))];

export const SHOWCASE_PROJECTS: GalleryProjectShowcase[] = [
  {
    id: 'nexa',
    title: 'Nexa',
    subtitle: 'Gestion inteligente para consultorio dental',
    cover: IMAGES.nexa,
    gallery: projectGallery('nexa', ['nexa2.png', 'nexa3.png', 'nexa4.png', 'nexa5.png']),
    technologies: ['React', 'TypeScript', 'Supabase', 'Dashboards', 'AI'],
    concept:
      'Una pieza limpia y precisa: interfaz de alto contraste, paneles claros y decisiones rapidas para operacion clinica.',
    clientNeed:
      'Centralizar agenda, historial y cobranza para eliminar friccion diaria y tener visibilidad completa de flujo de caja.',
    artisticNote:
      'Pensado como una mesa de control medica: sereno en apariencia, quirurgico en ejecucion.',
  },
  {
    id: 'hogar-maestro',
    title: 'Hogar Maestro',
    subtitle: 'Sistema ERP operativo',
    cover: IMAGES.hogarMaestro,
    gallery: projectGallery('hogar-maestro'),
    technologies: ['React', 'PostgreSQL', 'Auth', 'ERP Modules', 'Analytics'],
    concept:
      'Composicion modular con jerarquia fuerte para lectura instantanea de inventario, compras y finanzas.',
    clientNeed:
      'Optimizar ingreso/salida de productos, reducir errores manuales y entender con claridad donde se va el dinero.',
    artisticNote:
      'Visualmente se trata como arquitectura industrial: orden, capas y trazabilidad.',
  },
  {
    id: 'ggstore',
    title: 'GGSTORE',
    subtitle: 'Inventario y ventas en tiempo real',
    cover: IMAGES.ggstore,
    gallery: projectGallery('ggstore'),
    technologies: ['SvelteKit', 'PostgreSQL', 'Drizzle', 'POS Flows', 'Reporting'],
    concept:
      'UI de ritmo rapido para caja y almacen, con acentos de estado y acciones cortas de alta frecuencia.',
    clientNeed:
      'Acelerar venta en punto fisico y tener control fino de stock, costos y margen por producto.',
    artisticNote:
      'Diseñado como una bitacora viva: cada movimiento deja huella y contexto.',
  },
  {
    id: 'chulls',
    title: "CHULL'S",
    subtitle: 'Web gastronomica y reservas',
    cover: IMAGES.chulls,
    gallery: projectGallery('chulls'),
    technologies: ['React', 'Booking UX', 'CMS', 'Performance', 'SEO'],
    concept:
      'Una experiencia editorial con peso visual en fotografia y ritmo tipografico para abrir apetito.',
    clientNeed:
      'Mostrar propuesta culinaria con identidad fuerte y convertir visitas en reservas confirmadas.',
    artisticNote:
      'Se trabajo como carta de autor digital: atmosfera primero, conversion despues.',
  },
  {
    id: 'instarender',
    title: 'InstaRender',
    subtitle: 'Visualizaciones arquitectonicas con AI',
    cover: IMAGES.instarender,
    gallery: projectGallery('instarender'),
    technologies: ['Three.js', 'AI Pipelines', 'React', 'Cloud Render', 'Asset Optimization'],
    concept:
      'Escena inmersiva con narrativa visual de estudio creativo, enfocada en impacto inmediato.',
    clientNeed:
      'Reducir tiempos de previsualizacion y acelerar aprobaciones con renders de alta calidad.',
    artisticNote:
      'La interfaz actua como galeria curada: tecnica por dentro, expresiva por fuera.',
  },
  {
    id: 'lumbra',
    title: 'Lumbra',
    subtitle: 'Ingenier\u00eda y arquitectura para espacios habitables',
    cover: IMAGES.lumbra,
    gallery: projectGallery('lumbra', ['lumbra2.png', 'lumbra3.png']),
    technologies: ['React', 'Arquitectura', 'Landing page', 'Branding'],
    concept:
      'Una portada clara y estructural que comunica confianza t\u00e9cnica sin perder calidez humana.',
    clientNeed:
      'Presentar su estudio de ingenier\u00eda y arquitectura con una primera impresi\u00f3n contundente y profesional.',
    artisticNote:
      'Se toma el paisaje y la fotograf\u00eda como base para transmitir estabilidad estructural y serenidad en el hogar.',
  },

  {
    id: 'invitaciones',
    title: 'Invitaciones',
    subtitle: 'Gestion de invitaciones y RSVP',
    cover: IMAGES.invitaciones,
    gallery: projectGallery('invitaciones'),
    technologies: ['React', 'Three.js', 'RSVP', 'WebGL'],
    concept:
      'Interfaz con direccion artistica de evento, combinando dinamismo visual y claridad operacional.',
    clientNeed:
      'Controlar confirmaciones, asistencia y flujo de invitados desde una sola plataforma.',
    artisticNote:
      'Se construyo como una pieza escenica: informativa en estructura, emocional en presencia.',
  },
  {
    id: 'vetpe',
    title: 'VetPe',
    subtitle: 'Marketplace veterinario',
    cover: IMAGES.vetpe,
    gallery: projectGallery('vetpe', ['vetpe1.png', 'vetpe2.png']),
    technologies: ['Marketplace', 'Payments', 'Auth', 'Backoffice'],
    concept: 'Producto multirol para conectar demanda, proveedores y gestion operativa en un solo flujo.',
    clientNeed: 'Ordenar reservas, perfiles, pagos y administracion para servicios veterinarios.',
    artisticNote: 'La prioridad visual es confianza, lectura rapida y sensacion de producto listo para operar.',
  },
  {
    id: 'motorperu',
    title: 'MotorPerú',
    subtitle: 'Marketplace automotriz',
    cover: IMAGES.motorperu,
    gallery: projectGallery('motorperu', ['motorperu1.png', 'motorperu2.png']),
    technologies: ['Marketplace', 'Vehicles', 'Admin', 'Search'],
    concept: 'Ecosistema comercial para listar, filtrar y gestionar vehiculos con experiencia clara.',
    clientNeed: 'Convertir inventario y contactos en oportunidades trazables para compradores y vendedores.',
    artisticNote: 'Se plantea con energia comercial: velocidad, comparacion y decision.',
  },
  {
    id: 'midefensaperu',
    title: 'MiDefensaPerú',
    subtitle: 'Marketplace legal',
    cover: IMAGES.midefensaperu,
    gallery: projectGallery('midefensaperu', ['midefensaperu1.png', 'midefensaperu2.png']),
    technologies: ['Legal Tech', 'Marketplace', 'Backoffice', 'Multi-role'],
    concept: 'Plataforma para hacer mas accesible la conexion entre usuarios, abogados y casos.',
    clientNeed: 'Gestionar servicios legales, perfiles, solicitudes y administracion interna sin friccion.',
    artisticNote: 'Tono sobrio y confiable: claridad antes que adorno.',
  },
  {
    id: 'san-marcos',
    title: 'San Marcos Engine',
    subtitle: 'Dashboard educativo',
    cover: IMAGES.sanMarcos,
    gallery: projectGallery('san-marcos'),
    technologies: ['Dashboard', 'Analytics', 'Education', 'Simulation'],
    concept: 'Sistema analitico para convertir preparacion academica en datos utiles y accionables.',
    clientNeed: 'Medir rendimiento, simular escenarios y entender progreso con claridad.',
    artisticNote: 'Interfaz tipo cabina de control: informacion densa, organizada y accionable.',
  },
  {
    id: 'sunatfacil',
    title: 'SunatFácil',
    subtitle: 'Automatización tributaria',
    cover: IMAGES.sunatfacil,
    gallery: projectGallery('sunatfacil'),
    technologies: ['Automation', 'SaaS', 'Peru', 'Operations'],
    concept: 'Herramienta para transformar procesos tributarios complejos en pasos simples.',
    clientNeed: 'Reducir tiempo operativo y errores en consultas, comprobantes y gestiones administrativas.',
    artisticNote: 'La experiencia busca calma: menos friccion para tareas que suelen sentirse pesadas.',
  },
  {
    id: 'axon',
    title: 'Axon Landing',
    subtitle: 'Landing de producto AI',
    cover: IMAGES.axon,
    gallery: projectGallery('axon'),
    technologies: ['Landing', 'AI', 'Conversion', 'Copywriting'],
    concept: 'Pagina directa para explicar valor, diferenciar la propuesta y empujar accion.',
    clientNeed: 'Presentar un producto con claridad y validar interes con una estructura de conversion.',
    artisticNote: 'Ritmo breve, visual y persuasivo: que el usuario entienda antes de cansarse.',
  },
  {
    id: 'hermes-tech',
    title: 'Hermes Tech',
    subtitle: 'AI agents & workflows',
    cover: IMAGES.hermesTech,
    gallery: projectGallery('hermes-tech'),
    technologies: ['AI Agents', 'Automation', 'Workflows', 'Internal Tools'],
    concept: 'Sitio para comunicar automatizacion inteligente y agentes aplicados a operaciones reales.',
    clientNeed: 'Explicar una oferta tecnica compleja de forma simple, creible y orientada a negocio.',
    artisticNote: 'La direccion mezcla precision tecnica con sensacion de sistema vivo.',
  },
];

// Helper para codificar rutas de imágenes con caracteres especiales
const galleryImage = (path: string) => {
  // En Vite, las rutas en public/ se sirven desde la raíz
  // Codificar solo el nombre del archivo si tiene caracteres especiales
  const parts = path.split('/').filter(p => p);
  const fileName = parts.pop() || '';
  const directory = '/' + parts.join('/');
  
  // Solo codificar si el nombre tiene caracteres especiales
  const needsEncoding = /[\s()]/.test(fileName);
  const finalPath = needsEncoding 
    ? `${directory}/${encodeURIComponent(fileName)}`
    : path;
  
  return finalPath;
};

export const COLUMN_1_ITEMS: GalleryScrollItem[] = [
  { id: 'c1-nexa', projectId: 'nexa' },
  { id: 'c1-hogar', projectId: 'hogar-maestro' },
  { id: 'c1-ggstore', projectId: 'ggstore' },
  { id: 'c1-vetpe', projectId: 'vetpe' },
  { id: 'c1-motorperu', projectId: 'motorperu' },
  { id: 'c1-midefensaperu', projectId: 'midefensaperu' },
  { id: 'c1-san-marcos', projectId: 'san-marcos' },
];

export const COLUMN_2_ITEMS: GalleryScrollItem[] = [
  { id: 'c2-lumbra', projectId: 'lumbra' },
  { id: 'c2-chulls', projectId: 'chulls' },
  { id: 'c2-instarender', projectId: 'instarender' },
  { id: 'c2-invitaciones', projectId: 'invitaciones' },
  { id: 'c2-sunatfacil', projectId: 'sunatfacil' },
  { id: 'c2-axon', projectId: 'axon' },
  { id: 'c2-hermes-tech', projectId: 'hermes-tech' },
];

export const getShowcaseProjectById = (projectId: string): GalleryProjectShowcase | undefined =>
  SHOWCASE_PROJECTS.find((project) => project.id === projectId);
